import React from 'react';
import { createPortal } from 'react-dom';
import AssignmentReadOnlyModal from './AssignmentReadOnlyModal';
import LeaderExportModal from './export/LeaderExportModal';
import ConflictOverviewModal from './ConflictOverview/ConflictOverviewModal';
import AbsenceDetailModal from '../employee/AbsenceDetailModal';
import ContextCalendar from './ContextCalendar';
import AssignmentHoverCard from './AssignmentHoverCard';
import ConflictHoverCard from './ConflictHoverCard';

/**
 * LeaderModalsOrchestrator - Orchestriert das Rendern aller Modals im Leader Tool.
 * Extrahiert die komplexe Portal- und Conditional-Render Logik aus der Hauptseite.
 */
const LeaderModalsOrchestrator = ({
    // Navigation & Editor State
    modalOpen,
    isReadOnly,
    editingAssignment,
    editStack,
    forwardStack,
    presetDate,
    isUndatedPreset,
    navigationSource,
    
    // Modal Visibility
    exportOpen,
    conflictOverviewOpen,
    absenceModalOpen,
    
    // Data & Refs
    user,
    allAssignments,
    workers,
    vehicles,
    kolonnen,
    assignments,
    absences,
    assignmentsForConflicts,
    absencesForConflicts,
    categories,
    companyLoc,
    activeResources,
    activeDateRange,
    calculatedConflicts,
    logicalConflictCount,
    focusResource,
    absenceWorkerId,
    isConflictDetectionEnabled,
    panelRef,
    exportSelectedIds,
    exportInitialFilters,
    
    // Handlers
    onCloseEditor,
    onSaveAssignment,
    onDeleteAssignment,
    onResourcesChange,
    onDateRangeChange,
    onExportShortcut,
    onAssignmentClick,
    onAssignmentHover,
    onExportAssignmentHover,
    onAssignmentHoverEnd,
    onNavigateBack,
    onNavigateForward,
    onCloseExport,
    onCloseConflictOverview,
    onToggleDetection,
    onEditAssignmentFromConflict,
    onEditResourceFromConflict,
    onConflictActiveTabChange,
    onConflictExpandedResourcesChange,
    onConflictCollapsedSectionsChange,
    conflictActiveTab,
    conflictExpandedResources,
    conflictCollapsedSections,
    onCloseAbsenceModal,
    
    // Hover States & Handlers
    hoveredAssignment,
    hoveredAssignmentConflicts,
    hoverCardPos,
    hoveredConflict,
    hoveredConflictPos,
    interaction,
    onHoverCardEnter,
    onConflictHoverCardEnter,
    onConflictHoverEnd,
    onLoadData
}) => {
    return (
        <>
            {/* --- EXPORT MODAL --- */}
            {exportOpen && (
                <LeaderExportModal
                    isOpen={exportOpen}
                    onClose={onCloseExport}
                    assignments={allAssignments}
                    workers={workers}
                    vehicles={vehicles}
                    kolonnen={kolonnen}
                    isReadOnly={isReadOnly}
                    workerId={user?.worker_id}
                    initialSelectedIds={exportSelectedIds}
                    initialFilters={exportInitialFilters}
                    onAssignmentClick={onAssignmentClick}
                    onAssignmentHover={onExportAssignmentHover || onAssignmentHover}
                    onAssignmentHoverEnd={onAssignmentHoverEnd}
                    isCovered={modalOpen}
                    companyLoc={companyLoc}
                />
            )}

            {/* --- CONFLICT OVERVIEW --- */}
            {conflictOverviewOpen && (
                <ConflictOverviewModal
                    isOpen={conflictOverviewOpen}
                    onClose={onCloseConflictOverview}
                    conflicts={calculatedConflicts}
                    workers={workers}
                    vehicles={vehicles}
                    kolonnen={kolonnen}
                    assignments={assignments}
                    isDetectionEnabled={isConflictDetectionEnabled}
                    targetResource={focusResource}
                    onEditAssignment={onEditAssignmentFromConflict}
                    onEditResource={onEditResourceFromConflict}
                    onToggleDetection={onToggleDetection}
                    activeTab={conflictActiveTab}
                    onActiveTabChange={onConflictActiveTabChange}
                    expandedResources={conflictExpandedResources}
                    onExpandedResourcesChange={onConflictExpandedResourcesChange}
                    collapsedSections={conflictCollapsedSections}
                    onCollapsedSectionsChange={onConflictCollapsedSectionsChange}
                    logicalCountProp={logicalConflictCount}
                />
            )}

            {/* --- ABSENCE MODAL --- */}
            {absenceModalOpen && (
                <AbsenceDetailModal
                    isOpen={absenceModalOpen}
                    workerId={absenceWorkerId}
                    onClose={onCloseAbsenceModal}
                />
            )}

            {/* --- HOVER CARDS --- */}
            {hoveredAssignment && !interaction && (
                <AssignmentHoverCard
                    assignment={hoveredAssignment}
                    conflicts={hoveredAssignmentConflicts}
                    position={hoverCardPos}
                    workers={workers}
                    vehicles={vehicles}
                    kolonnen={kolonnen}
                    isConflictDetectionEnabled={isConflictDetectionEnabled && !hoverCardPos?.hideConflicts}
                    onMouseEnter={() => {
                        if (onHoverCardEnter) onHoverCardEnter();
                    }}
                    onMouseLeave={onAssignmentHoverEnd}
                    onAssignmentClick={onAssignmentClick}
                />
            )}

            {hoveredConflict && !interaction && (
                <ConflictHoverCard
                    conflict={hoveredConflict}
                    position={hoveredConflictPos}
                    onMouseEnter={() => {
                        if (onConflictHoverCardEnter) onConflictHoverCardEnter();
                    }}
                    onMouseLeave={onConflictHoverEnd}
                />
            )}
        </>
    );
};


export default LeaderModalsOrchestrator;
